import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Custom hook for managing infinite scroll data fetching
 * @param {Object} config - Configuration object
 * @param {Function} config.fetchFunction - Async function to fetch data (receives { pageParam, searchTerm })
 * @param {string} config.queryKey - Base query key for react-query
 * @param {boolean} config.enabled - Whether the query should be enabled
 * @param {number} config.pageSize - Number of items per page
 * @param {number} config.staleTime - How long data stays fresh (ms)
 * @param {number} config.gcTime - Garbage collection time (ms)
 * @param {number} config.searchDebounceMs - Debounce time for search
 * @returns {Object} - Hook return object with data and handlers
 */
export const useInfiniteDropdownData = ({
  fetchFunction,
  queryKey,
  enabled = true,
  pageSize = 20,
  staleTime = 5 * 60 * 1000,
  gcTime = 5 * 60 * 1000,
  onError,
  getNextPageParam,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const buildQueryKey = (key, searchTerm) => {
    const baseKey = Array.isArray(key) ? key : [key];
    const keyWithSearch = [...baseKey, searchTerm || undefined].filter(Boolean);
    return keyWithSearch;
  };

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: buildQueryKey(queryKey, searchTerm),
    queryFn: async ({ pageParam = 0 }) => {
      try {
        return await fetchFunction({
          pageParam,
          pageSize,
          searchTerm: searchTerm || undefined,
        });
      } catch (err) {
        if (onError) {
          onError(err);
        }
        throw err;
      }
    },
    initialPageParam: 0,
    getNextPageParam:
      getNextPageParam ||
      ((lastPage) =>
        lastPage?.last ? undefined : (lastPage?.number ?? -1) + 1),
    enabled,
    staleTime,
    gcTime,
    refetchOnWindowFocus: false,
  });

  // Flatten paginated data
  const flattenedData =
    data?.pages?.flatMap((page) => page.content || []) || [];

  return {
    data: flattenedData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    searchTerm,
    setSearchTerm,
    refetch,
    pageCount: data?.pages?.length || 0,
  };
};
